/*
<summary>
   This file is all about validation logic for the Tool Management Step 2 User Created Inspection tabs.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns module exports</returns>
*/

import {
  IToolCheckItem,
  IToolCrimpRow,
  IToolInspectionTab,
} from '../../../../models/forms/IToolInspection';

interface IHeaderGroup {
  Title: string;
  Items: IToolCheckItem[];
}

/**
 * This function is used to group a flat list of check items into header groups, preserving the order headers were first encountered.
 * @param items: Flat list of check items
 */
export const groupCheckItemsByHeader = (
  items: IToolCheckItem[]
): IHeaderGroup[] => {
  const groups: Record<string, IToolCheckItem[]> = {};
  const order: string[] = [];
  (items || []).forEach((item) => {
    const title = item.HeaderTitle || '';
    if (!groups[title]) {
      groups[title] = [];
      order.push(title);
    }
    groups[title].push(item);
  });
  return order.map((title) => ({ Title: title, Items: groups[title] }));
};

export const hasEmptyTabTitle = (tab: IToolInspectionTab): boolean =>
  (tab.Title || '').trim() === '';

export const hasNoImage = (tab: IToolInspectionTab): boolean =>
  !tab.Images || tab.Images.length === 0;

export const hasHeaderWithoutCheckItems = (tab: IToolInspectionTab): boolean => {
  if (tab.Headers && tab.Headers.length > 0) {
    return tab.Headers.some((h) => !h.Items || h.Items.length === 0);
  }
  const groups = groupCheckItemsByHeader(tab.CheckItems);
  if (groups.length === 0) return false;
  return groups.some((h) => !h.Items || h.Items.length === 0);
};

export const hasDuplicateHeaderNames = (tab: IToolInspectionTab): boolean => {
  const rawTitles =
    tab.Headers && tab.Headers.length > 0
      ? tab.Headers.map((h) => h.Title)
      : groupCheckItemsByHeader(tab.CheckItems).map((h) => h.Title);
  const titles = rawTitles.map((t) => t.trim().toLowerCase()).filter((t) => t !== '');
  return new Set(titles).size !== titles.length;
};

export const hasDuplicateCheckItemNames = (tab: IToolInspectionTab): boolean =>
  groupCheckItemsByHeader(tab.CheckItems).some((h) => {
    const texts = h.Items.map((i) => i.CheckItemMessage.trim().toLowerCase()).filter(
      (t) => t !== ''
    );
    return new Set(texts).size !== texts.length;
  });

export const hasEmptyCheckItemContent = (tab: IToolInspectionTab): boolean =>
  (tab.CheckItems || []).some((item) => item.CheckItemMessage.trim() === '');

export const hasDuplicateTabTitles = (tabs: IToolInspectionTab[]): boolean => {
  const titles = tabs.map((t) => t.Title.trim().toLowerCase()).filter((t) => t !== '');
  return new Set(titles).size !== titles.length;
};

export const isDuplicateTabTitle = (
  tab: IToolInspectionTab,
  tabs: IToolInspectionTab[]
): boolean => {
  const title = (tab.Title || '').trim().toLowerCase();
  if (!title) return false;
  return tabs.filter((t) => (t.Title || '').trim().toLowerCase() === title).length > 1;
};

/**
 * This function is used to check whether a single inspection tab has any validation error at all (title, image, headers or check items).
 * @param tab: Inspection tab to check
 * @param tabs: All inspection tabs, used to detect duplicate titles
 */
export const tabHasError = (
  tab: IToolInspectionTab,
  tabs?: IToolInspectionTab[]
): boolean =>
  hasEmptyTabTitle(tab) ||
  (tabs ? isDuplicateTabTitle(tab, tabs) : false) ||
  hasNoImage(tab) ||
  hasHeaderWithoutCheckItems(tab) ||
  hasDuplicateHeaderNames(tab) ||
  hasDuplicateCheckItemNames(tab) ||
  hasEmptyCheckItemContent(tab);

export interface IStepTwoValidationResult {
  isValid: boolean;
  messageKey?: string;
  isWarning?: boolean;
}

/**
 * This function is used to run all Step 2 validation rules for the User Created Inspection format and return the first rule that fails.
 * @param tabs: Inspection tabs to validate
 */
export const validateUserCreatedInspectionStep = (
  tabs: IToolInspectionTab[]
): IStepTwoValidationResult => {
  if (!tabs || tabs.length === 0) {
    return { isValid: false, messageKey: 'no_tabs_added' };
  }
  if (tabs.length > 5) {
    return { isValid: false, messageKey: 'max_tabs_allowed' };
  }
  if (tabs.some(hasEmptyTabTitle)) {
    return { isValid: false, messageKey: 'please_enter_tab_title' };
  }
  if (hasDuplicateTabTitles(tabs)) {
    return { isValid: false, messageKey: 'duplicate_found_tabs' };
  }
  if (tabs.some(hasNoImage)) {
    return { isValid: false, messageKey: 'please_add_tab_image' };
  }
  if (tabs.some(hasDuplicateCheckItemNames)) {
    return { isValid: false, messageKey: 'duplicate_item_in_header', isWarning: true };
  }
  if (tabs.some(hasEmptyCheckItemContent)) {
    return { isValid: false, messageKey: 'please_enter_check_content' };
  }
  if (tabs.some(hasHeaderWithoutCheckItems)) {
    return { isValid: false, messageKey: 'header_requires_check_item' };
  }
  if (tabs.some(hasDuplicateHeaderNames)) {
    return {
      isValid: false,
      messageKey: 'duplicate_header_names_not_allowed',
      isWarning: true,
    };
  }
  return { isValid: true };
};

// Crimp Tool Inspection (Slide 90): AWG/Upper Limit CH/Lower Limit CH/Tensile
// Strength Limit rows. Mirrors the per-field/per-row error helpers above so
// step-two-crimp-inspection.tsx can gate them behind the same
// touched/submit-attempted pattern as the User Created Inspection step.
export const hasEmptyAwg = (row: IToolCrimpRow): boolean =>
  (row.AWG || '').trim() === '';

/**
 * This function is used to check whether a crimp row's AWG value is a duplicate of another row's AWG.
 * @param row: Row to check
 * @param rows: All crimp rows, used to detect duplicates
 */
export const isDuplicateAwg = (row: IToolCrimpRow, rows: IToolCrimpRow[]): boolean => {
  const awg = (row.AWG || '').trim().toLowerCase();
  if (!awg) return false;
  return rows.filter((r) => (r.AWG || '').trim().toLowerCase() === awg).length > 1;
};

export const hasEmptyUpperLimit = (row: IToolCrimpRow): boolean =>
  row.UpperLimitCH === null || row.UpperLimitCH === undefined || row.UpperLimitCH === '';

export const hasEmptyLowerLimit = (row: IToolCrimpRow): boolean =>
  row.LowerLimitCH === null || row.LowerLimitCH === undefined || row.LowerLimitCH === '';

export const hasEmptyTensileStrength = (row: IToolCrimpRow): boolean =>
  row.TensileStrengthLimit === null ||
  row.TensileStrengthLimit === undefined ||
  row.TensileStrengthLimit === '';

export const isUpperLessThanLower = (row: IToolCrimpRow): boolean =>
  !hasEmptyUpperLimit(row) &&
  !hasEmptyLowerLimit(row) &&
  Number(row.UpperLimitCH) < Number(row.LowerLimitCH);

const INT32_MAX_VALUE = 2147483647;

export const isUpperLimitTooLarge = (row: IToolCrimpRow): boolean =>
  !hasEmptyUpperLimit(row) && Number(row.UpperLimitCH) > INT32_MAX_VALUE;

export const isLowerLimitTooLarge = (row: IToolCrimpRow): boolean =>
  !hasEmptyLowerLimit(row) && Number(row.LowerLimitCH) > INT32_MAX_VALUE;

export const isTensileStrengthInvalid = (row: IToolCrimpRow): boolean =>
  !hasEmptyTensileStrength(row) &&
  (!Number.isInteger(Number(row.TensileStrengthLimit)) || Number(row.TensileStrengthLimit) <= 0);

export const isTensileStrengthTooLarge = (row: IToolCrimpRow): boolean =>
  !hasEmptyTensileStrength(row) && Number(row.TensileStrengthLimit) > INT32_MAX_VALUE;

/**
 * This function is used to check whether a single crimp row has any validation error at all.
 * @param row: Row to check
 * @param rows: All crimp rows, used to detect duplicate AWG values
 */
export const crimpRowHasError = (row: IToolCrimpRow, rows: IToolCrimpRow[]): boolean =>
  hasEmptyAwg(row) ||
  isDuplicateAwg(row, rows) ||
  hasEmptyUpperLimit(row) ||
  hasEmptyLowerLimit(row) ||
  hasEmptyTensileStrength(row) ||
  isUpperLessThanLower(row) ||
  isUpperLimitTooLarge(row) ||
  isLowerLimitTooLarge(row) ||
  isTensileStrengthInvalid(row) ||
  isTensileStrengthTooLarge(row);

/**
 * This function is used to run all Step 2 validation rules for the Crimp Tool Inspection format and return the first rule that fails.
 * @param rows: Crimp rows to validate
 */
export const validateCrimpInspectionStep = (
  rows: IToolCrimpRow[]
): IStepTwoValidationResult => {
  if (!rows || rows.length === 0) {
    return { isValid: false, messageKey: 'please_add_crimp_row' };
  }
  if (rows.some(hasEmptyAwg)) {
    return { isValid: false, messageKey: 'please_enter_awg' };
  }
  if (rows.some((row) => isDuplicateAwg(row, rows))) {
    return { isValid: false, messageKey: 'duplicate_awg_not_allowed' };
  }
  if (rows.some(hasEmptyUpperLimit)) {
    return { isValid: false, messageKey: 'please_enter_upper_limit' };
  }
  if (rows.some(hasEmptyLowerLimit)) {
    return { isValid: false, messageKey: 'please_enter_lower_limit' };
  }
  if (rows.some(isUpperLessThanLower)) {
    return { isValid: false, messageKey: 'upper_limit_less_than_lower' };
  }
  if (rows.some(isUpperLimitTooLarge)) {
    return { isValid: false, messageKey: 'upper_limit_exceeds_max_value' };
  }
  if (rows.some(isLowerLimitTooLarge)) {
    return { isValid: false, messageKey: 'lower_limit_exceeds_max_value' };
  }
  if (rows.some(hasEmptyTensileStrength)) {
    return { isValid: false, messageKey: 'please_enter_tensile_strength' };
  }
  if (rows.some(isTensileStrengthInvalid)) {
    return { isValid: false, messageKey: 'tensile_strength_must_be_positive_integer' };
  }
  if (rows.some(isTensileStrengthTooLarge)) {
    return { isValid: false, messageKey: 'tensile_strength_exceeds_max_value' };
  }
  return { isValid: true };
};
